import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import * as Tone from "tone";
import { useDAWStore } from "../../store/dawStore";
import engine from "../../audio/AudioEngine";
import { useAuth } from "../../auth/AuthContext";
import { vocalRecorder } from "../../audio/VocalRecorder";
import API from "../../api";
import Knob from "./Knob";
import { Play, Pause, Stop, Record, FloppyDisk, FolderOpen, DownloadSimple, ArrowCounterClockwise, Metronome, Scissors, Cursor, CloudArrowUp, X, GearSix, SpeakerHigh, Keyboard, SpinnerGap } from "@phosphor-icons/react";
import { toast } from "sonner";
import audioBufferToWav from "audiobuffer-to-wav";
import { audioBufferToMp3 } from "../../utils/audioBufferToMp3";
import { CaretDown } from "@phosphor-icons/react";
import ProfileCircle from "../../auth/ProfileCircle";
import ThemeToggle from "../ThemeToggle";
import ProducerPaywallModal from "../ProducerPaywallModal";
import ShortcutListOverlay from "./ShortcutListOverlay";
import GlobalNav from "../GlobalNav";

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  const ms = Math.floor((seconds * 100) % 100);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}:${String(ms).padStart(2, "0")}`;
}

// v14.0.1 — freely-typeable BPM input. Keeps a local string buffer so
// the user can clear the field and type new digits without the input's
// value being clamped away underneath their cursor on every keystroke.
// Commits (clamp 40-300 + push to store/Transport) only on blur or when
// Enter is pressed. External BPM changes (knob rotation, project load)
// flow back into the buffer via the useEffect below.
function BpmInput({ bpm, onCommit }) {
  const [local, setLocal] = useState(String(Math.round(bpm)));
  useEffect(() => { setLocal(String(Math.round(bpm))); }, [bpm]);
  const commit = () => {
    const parsed = parseInt(local, 10);
    const clamped = Number.isFinite(parsed) ? Math.max(40, Math.min(300, parsed)) : Math.round(bpm);
    setLocal(String(clamped));
    onCommit(clamped);
  };
  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={local}
      onChange={(e) => setLocal(e.target.value.replace(/[^0-9]/g, ""))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.currentTarget.blur(); }
        else if (e.key === "Escape") { setLocal(String(Math.round(bpm))); e.currentTarget.blur(); }
      }}
      className="w-14 bg-[#111] border border-[#262626] rounded px-1.5 py-1 text-[11px] font-mono text-white/90 text-center focus:outline-none focus:border-[#444]"
      title="Type an exact BPM (40-300). Press Enter to apply."
      data-testid="bpm-input"
    />
  );
}

export default function TopBar({ onExport }) {  const { bpm, isPlaying, isRecording, setIsRecording, setBpm, setIsPlaying, projectName, setProjectName,
          saveProject, loadProject, resetProject,
          tracks, regions, totalBars,
          metronomeOn, setMetronomeOn,
          toolMode, setToolMode, undo, _history,
          setProjectId, lastAutoSavedAt,
          addAudioClip,
          hydrateFromProject,
          hydratingSamples } = useDAWStore();
  const { isAdmin, hasProducerAccess, firstProjectId, user, refresh: refreshAuth } = useAuth();
  // v19.0 — Producer Package paywall state. Triggered on 402 responses
  // from POST /projects (create) and by the manual cloud-save button
  // when a trial user tries to save a NEW project beyond their trial.
  const [showPaywall, setShowPaywall] = useState(false);
  // v17.0 — .crow file importer. Opens a hidden <input type=file>
  // when the user clicks the Import button; on file select, parses
  // the JSON and hydrates the DAW via hydrateFromProject.
  const importInputRef = useRef(null);
  const handleImportCrow = () => {
    if (importInputRef.current) importInputRef.current.click();
  };
  const onImportFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      // .crow schema: { __crow__:"project", version:1, projectName, bpm, tracks, regions, savedAt }
      // Also tolerate the raw server-side shape { name, bpm, state:{...} }.
      const state = payload.state ? payload.state : { tracks: payload.tracks || [], regions: payload.regions || [] };
      const project = {
        id: payload.id || null,
        name: payload.projectName || payload.name || "Imported Project",
        bpm: payload.bpm || state.bpm || 120,
        state: { ...state, tracks: payload.tracks || state.tracks || [], regions: payload.regions || state.regions || [] },
      };
      hydrateFromProject(project);
      toast.success(`Imported "${project.name}"`);
    } catch (err) {
      toast.error("Import failed: " + (err?.message || "invalid .crow file"));
    } finally {
      // Allow re-importing the same file if the user cancels+re-picks.
      if (importInputRef.current) importInputRef.current.value = "";
    }
  };
  const [time, setTime] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportMenuPos, setExportMenuPos] = useState({ top: 60, right: 16 });
  const exportMenuAnchorRef = useRef(null);
  // v17.5.1 — Keep the portalled menu anchored to the caret button on scroll
  // and window resize so it doesn't detach visually.
  useEffect(() => {
    if (!showExportMenu) return;
    const reposition = () => {
      const btn = exportMenuAnchorRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      setExportMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    };
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [showExportMenu]);
  // v17.5 — Close the export dropdown on outside click / Escape.
  useEffect(() => {
    if (!showExportMenu) return;
    const onDoc = (e) => {
      if (
        !e.target.closest?.("[data-testid='export-dropdown']") &&
        !e.target.closest?.("[data-testid='export-menu']")
      ) setShowExportMenu(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setShowExportMenu(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [showExportMenu]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveDialogName, setSaveDialogName] = useState("");
  const [saveDialogGenre, setSaveDialogGenre] = useState("pop");
  const [saving, setSaving] = useState(false);
  // v19.7 — Keyboard shortcut cheatsheet visibility.
  const [showShortcuts, setShowShortcuts] = useState(false);
  const rafRef = useRef(null);

  // Global Ctrl+Z / Cmd+Z: pop the last history snapshot. Skipped when the
  // focused element is a text input so the browser's native undo (in inputs)
  // still works as expected.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return;
      if (e.key !== "z" && e.key !== "Z") return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      e.preventDefault();
      const ok = undo();
      if (ok) toast.info("Undone");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo]);

  // Cloud save — snapshot the DAW state, strip local blob: URLs from any
  // sampler tracks (per user choice: local blobs don't persist across
  // reloads), POST to /api/projects, close dialog on success.
  const handleCloudSave = async () => {
    if (saving) return;
    const name = (saveDialogName || projectName || "Untitled Project").trim();
    if (!name) { toast.error("Project name required"); return; }
    setSaving(true);
    try {
      const stripped = tracks.map((t) => {
        // Strip local blob URLs — they die on reload. Kit-sourced samples (URLs
        // starting with /api/static/) survive because the backend serves them
        // as StaticFiles from persistent storage.
        if (t.sampleUrl && t.sampleUrl.startsWith("blob:")) {
          return { ...t, sampleUrl: null, sampleName: null, samplerLoadError: null };
        }
        return t;
      });
      const state = {
        projectName: name,
        bpm,
        tracks: stripped,
        regions,
      };
      const { data } = await API.post("/projects", { name, bpm, genre: saveDialogGenre, state });
      // v10.3 — persist returned projectId so subsequent auto-saves PUT
      // to /api/projects/{id} instead of creating a new row every 60s.
      if (data?.project?.id) setProjectId(data.project.id);
      setShowSaveDialog(false);
      setSaveDialogName("");
      toast.success(`Saved "${name}" to your Projects`);
      // Refresh the auth user so `first_project_id` propagates into
      // AuthContext — subsequent "Create Project" clicks now hit the
      // paywall correctly.
      try { await refreshAuth?.(); } catch { /* noop */ }
    } catch (e) {
      // v19.0 — Producer Package paywall: backend returns 402 when the
      // user has already claimed their trial slot. Surface the upgrade
      // modal instead of a generic toast.
      if (e?.response?.status === 402) {
        setShowSaveDialog(false);
        setShowPaywall(true);
      } else {
        toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const tick = () => {
      if (engine.initialized) setTime(engine.getSeconds());
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const handlePlay = async () => {
    if (isPlaying) {
      engine.pause();
      setIsPlaying(false);
    } else {
      // v21.2 — Flip isPlaying BEFORE the async awaits so a rapid
      // second Play-button click hits the `if (isPlaying)` guard on
      // this handler's next invocation and takes the pause path
      // instead of stacking a second play(). engine.play() also has
      // its own re-entry lock as belt-and-braces.
      setIsPlaying(true);
      // v19.20 — Capture the transport position BEFORE any setup call
      // so a ruler-click positioning at, say, bar 13 is honoured on
      // Play even if setupSequencer's disposeSequence / rebuild path
      // resets the transport. Previously the position could drift back
      // to 0 between the click and the actual t.start(), so Play always
      // felt like it started from bar 1.
      let startSec = 0;
      try { startSec = Tone.getTransport().seconds || 0; } catch {}
      // Wire up sequencer + audio-clip schedule BEFORE starting the transport,
      // otherwise Tone.Sequence.start(0) fires against a already-running
      // transport clock and gets scheduled in the past → sequence stays
      // 'stopped' and MIDI regions never trigger. See iter 20 RCA.
      await engine.init();
      await engine.ensureRunning();
      engine.setupSequencer(tracks, bpm, regions);
      await engine.play({ startSec });
    }
  };

  const handleStop = () => {
    engine.stop();
    setIsPlaying(false);
    setTime(0);
  };

  const handleSave = () => {
    // v13.1 — "Save" button downloads the project as a .crow JSON file
    // to the user's computer instead of writing to browser IndexedDB.
    // (Cloud save + local IndexedDB auto-save still exist under other
    //  buttons — this makes the primary Save action a portable backup.)
    try {
      const stripped = tracks.map((t) => {
        if (t.sampleUrl && t.sampleUrl.startsWith("blob:")) {
          return { ...t, sampleUrl: null, sampleName: null };
        }
        return t;
      });
      const payload = {
        __crow__: "project",
        version: 1,
        projectName: projectName || "Untitled Project",
        bpm,
        tracks: stripped,
        regions,
        savedAt: new Date().toISOString(),
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(projectName || "project").replace(/\s+/g, "_")}.crow`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(".crow file saved to your computer");
    } catch (e) {
      toast.error("Save failed: " + (e?.message || "unknown"));
    }
  };

  const handleLoad = () => {
    const ok = loadProject();
    toast[ok ? "success" : "error"](ok ? "Project loaded" : "No saved project found");
  };

  // v13.1 — AI Beat button removed from TopBar (both duplicates); no handler needed.


  const handleExport = async (format = "wav") => {
    try {
      setExporting(true);
      toast.info(format === "mp3" ? "Rendering audio → MP3..." : "Rendering audio...");
      await engine.init();
      // v17.5 — Both WAV and MP3 use the SAME offline render so the FX
      // chain, sound design and arrangement are bit-identical up to the
      // final encoder; only the encoding step differs.
      const buffer = await engine.exportToWav(tracks, bpm, regions, totalBars);
      let blob, ext, mime;
      if (format === "mp3") {
        blob = audioBufferToMp3(buffer);
        ext = "mp3";
        mime = "audio/mpeg";
      } else {
        const wav = audioBufferToWav(buffer);
        blob = new Blob([wav], { type: "audio/wav" });
        ext = "wav";
        mime = "audio/wav";
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${projectName.replace(/\s+/g, "_")}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Export complete (${ext.toUpperCase()})!`);
      onExport && onExport({ format: ext, mime, duration: buffer.duration });
    } catch (e) {
      toast.error("Export failed: " + e.message);
    } finally {
      setExporting(false);
    }
  };

  // v13.1 — Stem Export: per-track WAV stems bundled into a single .zip
  // download via JSZip. Uses engine.exportStems which renders each track
  // in isolation through its full FX chain (Reverb, Delay, Crow Pumper,
  // EQ, reversed audio state, synth config). Skips empty tracks.
  // v14.1 — Vocal Recording. Toggles mic capture. Any audio track with
  // `recordArmed` receives the resulting WAV clip at the transport bar
  // where recording started. Multiple armed tracks all get the same
  // clip (useful for stacking or A/B).
  const recordStartBarRef = useRef(0);
  const handleToggleRecord = async () => {
    if (isRecording) {
      // ---- STOP ---------------------------------------------------
      try {
        const result = await vocalRecorder.stop();
        setIsRecording(false);
        if (!result) {
          toast.warning("No audio captured");
          return;
        }
        const url = URL.createObjectURL(result.wavBlob);
        const bpmNow = engine.getBpm() || bpm || 120;
        const secPerBar = (60 / bpmNow) * 4;
        const lengthBars = Math.max(1, Math.ceil(result.duration / secPerBar));
        const armed = (useDAWStore.getState().tracks || []).filter((t) => t.type === "audio" && t.recordArmed);
        if (armed.length === 0) {
          toast.warning("No armed track — clip discarded");
          return;
        }
        // Drop the same recorded clip on every armed track.
        const stamp = Date.now();
        armed.forEach((t, i) => {
          const clipId = `rec_${stamp}_${i}`;
          addAudioClip(t.id, {
            id: clipId,
            bar: recordStartBarRef.current,
            length: lengthBars,
            // v14.1.1 — audio clips use `url` + `name` (NOT sampleUrl/
            // sampleName which is the sampler schema). Also load the
            // buffer into AudioEngine so playback actually happens —
            // just writing metadata to the store isn't enough.
            url: url,
            name: `Take ${new Date().toLocaleTimeString()}.wav`,
            duration: result.duration,
            peaks: result.peaks,
            peaksFormat: result.peaksFormat,
          });
          try { engine.loadAudioClip(t.id, clipId, url); } catch (e) { /* engine may still be initialising */ }
        });
        toast.success(`Recorded ${result.duration.toFixed(1)}s → ${armed.length} track${armed.length === 1 ? "" : "s"}`);
      } catch (e) {
        setIsRecording(false);
        toast.error("Recording failed: " + (e?.message || "unknown"));
      }
      return;
    }
    // ---- START -----------------------------------------------------
    const armed = tracks.filter((t) => t.type === "audio" && t.recordArmed);
    if (armed.length === 0) {
      toast.info("Arm at least one audio track first (click the red R on a track header)");
      return;
    }
    try {
      await vocalRecorder.start();
      // Remember the transport bar where recording began so the
      // resulting clip lands where the playhead was.
      const bpmNow = engine.getBpm() || bpm || 120;
      const sec = engine.getSeconds ? engine.getSeconds() : 0;
      recordStartBarRef.current = Math.max(0, (sec * bpmNow) / 60 / 4);
      setIsRecording(true);
      toast.info("Recording… click Record again to stop");
    } catch (e) {
      const msg = e?.name === "NotAllowedError"
        ? "Microphone permission denied"
        : (e?.message || "Failed to access mic");
      toast.error(msg);
    }
  };
  // Release the mic when the DAW is torn down (page unmount / logout).
  useEffect(() => () => vocalRecorder.dispose(), []);
  const [exportingStems, setExportingStems] = useState(false);
  const handleExportStems = async () => {
    if (exportingStems) return;
    try {
      setExportingStems(true);
      toast.info("Rendering stems… this may take a moment");
      await engine.init();
      const stems = await engine.exportStems(tracks, bpm);
      if (stems.length === 0) {
        toast.error("No tracks with content to export as stems");
        return;
      }
      // Lazy-load JSZip so the initial bundle stays lean.
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();
      for (const s of stems) {
        const wav = audioBufferToWav(s.buffer);
        zip.file(s.name, wav);
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${projectName.replace(/\s+/g, "_")}_stems.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${stems.length} stem${stems.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error("Stem export failed: " + (e?.message || "unknown"));
    } finally {
      setExportingStems(false);
    }
  };

  // v13.1 — Save to Computer: JSON download of the current project state
  // as a .crow file (offline backup + share). Mirrors what the cloud
  // save endpoint stores, minus any local blob: URLs which would be
  // dead across reloads.
  const handleSaveToComputer = () => {
    try {
      const stripped = tracks.map((t) => {
        if (t.sampleUrl && t.sampleUrl.startsWith("blob:")) {
          return { ...t, sampleUrl: null, sampleName: null };
        }
        return t;
      });
      const payload = {
        __crow__: "project",
        version: 1,
        projectName: projectName || "Untitled Project",
        bpm,
        tracks: stripped,
        regions,
        savedAt: new Date().toISOString(),
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(projectName || "project").replace(/\s+/g, "_")}.crow`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Saved .crow file to your computer");
    } catch (e) {
      toast.error("Save to computer failed: " + (e?.message || "unknown"));
    }
  };

  return (
    <div className="h-14 px-4 flex items-center gap-4 border-b border-[#262626] bg-[#0e0e0e] overflow-x-auto overflow-y-hidden flex-shrink-0 w-full min-w-0 topbar-scroll sticky top-0 z-[100] shadow-lg shadow-black/60" data-testid="topbar">
      {/* Logo + name — v17.2 compact branding; v18.1.1 replaced blob PNG
          with the official standing-crow asset. */}
      <div className="flex items-center gap-2">
        <img
          src="/crow-standing.jpg"
          alt="CROW"
          width={28}
          height={28}
          style={{ display: "block", objectFit: "contain", borderRadius: 4 }}
          data-testid="topbar-crow-logo"
        />
        <div className="font-display font-extrabold text-[17px] tracking-[0.08em] leading-none">CROW</div>
      </div>

      <GlobalNav />

      <div className="w-px h-9 bg-[#262626] mx-1" />

      {/* Transport */}
      <div className="flex items-center gap-2">
        <button
          className={`btn-3d w-11 h-11 flex items-center justify-center ${isPlaying ? "btn-3d-accent" : ""} ${hydratingSamples ? "opacity-50 cursor-wait" : ""}`}
          onClick={handlePlay}
          disabled={hydratingSamples}
          data-testid="transport-play"
          title={hydratingSamples ? "Loading samples…" : (isPlaying ? "Pause" : "Play")}
        >
          {hydratingSamples ? (
            <SpinnerGap size={18} weight="bold" className="animate-spin" />
          ) : isPlaying ? (
            <Pause size={18} weight="fill" />
          ) : (
            <Play size={18} weight="fill" />
          )}
        </button>
        <button
          className="btn-3d w-11 h-11 flex items-center justify-center"
          onClick={handleStop}
          data-testid="transport-stop"
          title="Stop"
        >
          <Stop size={18} weight="fill" />
        </button>
        <button
          className={`btn-3d-red btn-3d w-11 h-11 flex items-center justify-center ${isRecording ? "animate-pulse" : ""}`}
          data-testid="transport-record"
          title={isRecording ? "Stop recording" : "Arm audio tracks (R) then click to record from mic"}
          onClick={handleToggleRecord}
        >
          <Record size={18} weight="fill" />
        </button>
      </div>

      {/* Time display */}
      <div className="time-display px-4 py-2 text-2xl tracking-wider" data-testid="time-display">
        {formatTime(time)}
      </div>

      {/* BPM — v14.0.1: knob visual + freely-typeable number input.
          The input uses a LOCAL string buffer so the user can clear "40"
          and type "150" (or empty string, or partial "12") without the
          onChange clamp fighting them. Value is committed to the store
          + Transport ONLY on blur or Enter. External BPM changes (knob,
          project load) sync into the buffer via a useEffect. */}
      <div className="flex items-center gap-3 px-3 py-1.5 rounded-md bg-[#0a0a0a] border border-[#222]">
        <div className="label-mono">BPM</div>
        <Knob
          value={bpm}
          min={40} max={300} step={1}
          size={40}
          onChange={(v) => { setBpm(v); engine.setBpm(v); }}
          format={(v) => `${Math.round(v)}`}
          data-testid="bpm-knob"
        />
        <BpmInput bpm={bpm} onCommit={(v) => { setBpm(v); engine.setBpm(v); }} />
      </div>

      {/* Metronome */}
      <button
        onClick={async () => {
          await engine.init();
          const next = !metronomeOn;
          setMetronomeOn(next);
          engine.setMetronome(next);
        }}
        className={`btn-3d w-11 h-11 flex items-center justify-center ${metronomeOn ? "btn-3d-accent" : ""}`}
        title={metronomeOn ? "Metronome ON" : "Metronome OFF"}
        data-testid="metronome-btn"
      >
        <Metronome size={18} weight={metronomeOn ? "fill" : "bold"} />
      </button>

      {/* Tool: Cursor / Scissor */}
      <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#0a0a0a] border border-[#222]" data-testid="tool-group">
        <button
          onClick={() => setToolMode("default")}
          className={`btn-3d w-9 h-9 flex items-center justify-center ${toolMode === "default" ? "btn-3d-accent" : ""}`}
          title="Selection tool (V)"
          data-testid="tool-cursor"
        >
          <Cursor size={16} weight="bold" />
        </button>
        <button
          onClick={() => setToolMode(toolMode === "scissor" ? "default" : "scissor")}
          className={`btn-3d w-9 h-9 flex items-center justify-center ${toolMode === "scissor" ? "btn-3d-accent" : ""}`}
          title="Scissor tool (C) — click a clip/region to split at that point"
          data-testid="tool-scissor"
        >
          <Scissors size={16} weight="bold" />
        </button>
      </div>

      {/* v19.7.1 — Keyboard-shortcut cheatsheet button. Placed here
          (BEFORE the flex-1 spacer) so it's part of the left cluster
          and never scrolls out of view on narrow viewports. Distinct
          teal-accent styling makes it easy to spot; `flex-shrink-0`
          guarantees the label stays intact under any layout pressure. */}
      <button
        className="btn-3d h-10 px-3 flex items-center gap-1.5 text-[11px] font-bold tracking-wide flex-shrink-0"
        style={{ color: "#34d399", border: "1px solid #10b98166" }}
        onClick={() => setShowShortcuts((v) => !v)}
        title="Keyboard shortcuts (Esc closes)"
        data-testid="shortcut-list-button"
      >
        <Keyboard size={14} weight="bold" />
        Shortcut List
      </button>

      <div className="flex-1" />

      {/* Project name */}
      <input
        type="text"
        value={projectName}
        onChange={(e) => setProjectName(e.target.value)}
        className="bg-[#0a0a0a] border border-[#222] rounded-md px-3 py-2 text-sm w-56 focus:outline-none focus:border-[#444] font-mono"
        data-testid="project-name-input"
      />

      {/* v10.3 — Auto-save indicator. Fades in briefly after each background
          POST/PUT to /api/projects so producers see their work is being
          persisted without any modal interruption. */}
      <AutoSaveIndicator lastAutoSavedAt={lastAutoSavedAt} />

      {/* v13.1 — Second AI Beat button REMOVED (duplicate). */}

      {/* Project Actions */}
      <div className="flex items-center gap-1">
        <button
          className="btn-3d w-10 h-10 flex items-center justify-center disabled:opacity-40"
          onClick={() => { const ok = undo(); if (ok) toast.info("Undone"); }}
          disabled={(_history || []).length === 0}
          title={`Undo (Ctrl+Z) · ${(_history || []).length} step${(_history || []).length === 1 ? "" : "s"}`}
          data-testid="undo-button"
        >
          <ArrowCounterClockwise size={16} weight="bold" />
        </button>
        {/* v17.0 — Server "Save to Profile" was admin-only.
            v19.0 — Now visible for every authenticated producer. The
            backend enforces the trial-project paywall (returns 402);
            we intercept 402 in handleCloudSave to surface the upgrade
            modal instead of a save-failed toast. */}
        {user && (
          <button
            className="btn-3d w-10 h-10 flex items-center justify-center"
            onClick={() => {
              // Producer/admin: skip straight to the save dialog.
              // Trial user creating a SECOND cloud project? Route to
              // paywall instead of the save dialog.
              if (!hasProducerAccess && firstProjectId && !useDAWStore.getState().projectId) {
                setShowPaywall(true);
                return;
              }
              setSaveDialogName(projectName || "");
              setShowSaveDialog(true);
            }}
            title="Save project to cloud"
            data-testid="cloud-save-button"
          >
            <CloudArrowUp size={16} weight="bold" />
          </button>
        )}
        <button className="btn-3d w-10 h-10 flex items-center justify-center" onClick={handleSave} title="Save (local)" data-testid="save-button">
          <FloppyDisk size={16} weight="bold" />
        </button>
        <button
          className="btn-3d w-10 h-10 flex items-center justify-center"
          onClick={handleSaveToComputer}
          title="Save project as .crow file to your computer"
          data-testid="save-to-computer-button"
        >
          <DownloadSimple size={16} weight="bold" />
        </button>
        {/* v17.0 — Import .crow file button + hidden <input>.
            v17.5 — Made explicitly universal: available to every user
            (guests and standard producers included) so anyone can reload
            a project file they downloaded earlier. NEVER gate this
            behind isAdmin. */}
        <button
          className="btn-3d w-10 h-10 flex items-center justify-center"
          onClick={handleImportCrow}
          title="Open .crow project file from your computer"
          data-testid="import-crow-button"
        >
          <FolderOpen size={16} weight="bold" />
        </button>
        <input
          ref={importInputRef}
          type="file"
          accept=".crow,application/json"
          className="hidden"
          onChange={onImportFileChosen}
          data-testid="import-crow-input"
        />
        <button className="btn-3d w-10 h-10 flex items-center justify-center" onClick={handleLoad} title="Load (local)" data-testid="load-button">
          <FolderOpen size={16} weight="bold" />
        </button>
        <button
          className="btn-3d h-10 px-3 flex items-center justify-center text-[11px] font-bold tracking-wide"
          onClick={() => { resetProject(); toast.info("Project reset"); }}
          title="Reset the project (clears all tracks & regions)"
          data-testid="reset-button"
        >
          Reset
        </button>
      </div>

      {/* v17.5 — Export dropdown: WAV (default click) + MP3 (caret).
          v17.5.1 HOTFIX — TopBar has `overflow-y-hidden`, which clipped the
          absolutely-positioned menu below the h-14 bar. Switched to a
          document.body portal with `position: fixed` anchored to the
          toggle button's bounding rect, so the menu escapes the clip
          entirely and appears in front of every DAW pane (z-[1000]). */}
      <div className="relative flex" data-testid="export-dropdown">
        <button
          className="btn-3d-accent btn-3d pl-4 pr-3 h-11 flex items-center gap-2 text-sm font-bold rounded-r-none border-r border-black/40"
          onClick={() => handleExport("wav")}
          disabled={exporting}
          data-testid="export-button"
          title="Export mixdown as .wav (lossless)"
        >
          <DownloadSimple size={16} weight="bold" />
          {exporting ? "Rendering..." : "Export WAV"}
        </button>
        <button
          ref={exportMenuAnchorRef}
          className="btn-3d-accent btn-3d px-2 h-11 flex items-center justify-center rounded-l-none"
          onClick={(e) => {
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            setExportMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
            setShowExportMenu((v) => !v);
          }}
          disabled={exporting}
          title="More export formats (WAV / MP3)"
          aria-label="More export formats"
          data-testid="export-menu-toggle"
        >
          <CaretDown size={14} weight="bold" />
        </button>
      </div>
      {showExportMenu && ReactDOM.createPortal(
        <div
          className="min-w-[200px] rounded-md border border-[#2a2a2a] bg-[#0e0e0e] shadow-2xl overflow-hidden"
          style={{ position: "fixed", top: exportMenuPos.top, right: exportMenuPos.right, zIndex: 1000 }}
          data-testid="export-menu"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => { setShowExportMenu(false); handleExport("wav"); }}
            className="w-full flex items-center justify-between gap-3 px-3 py-2 text-[12px] font-mono text-white/80 hover:bg-[#1a1a1a] hover:text-[#00FF41] transition-colors"
            data-testid="export-wav-menu-item"
          >
            <span>Export WAV</span>
            <span className="text-[9px] text-white/40">lossless</span>
          </button>
          <button
            onClick={() => { setShowExportMenu(false); handleExport("mp3"); }}
            className="w-full flex items-center justify-between gap-3 px-3 py-2 text-[12px] font-mono text-white/80 hover:bg-[#1a1a1a] hover:text-[#00FF41] transition-colors border-t border-[#1a1a1a]"
            data-testid="export-mp3-menu-item"
          >
            <span>Export MP3</span>
            <span className="text-[9px] text-white/40">192 kbps</span>
          </button>
        </div>,
        document.body,
      )}

      {/* v13.1 — Export Stems: per-track WAV stems bundled as .zip */}
      <button
        className="btn-3d px-4 h-11 flex items-center gap-2 text-sm font-bold"
        onClick={handleExportStems}
        disabled={exportingStems}
        title="Export each track as its own WAV stem, bundled into a .zip"
        data-testid="export-stems-button"
      >
        <DownloadSimple size={16} weight="bold" />
        {exportingStems ? "Rendering stems…" : "Export Stems"}
      </button>

      {/* v12.5 — Global theme toggle. Sun in dark mode, moon in light. */}
      <ThemeToggle />

      {/* v11.3 — Audio device selector. Enumerates OS audio outputs
          (speakers, headphones, Bluetooth, external monitor) and routes
          the Tone.js AudioContext through the chosen one via setSinkId.
          Selection persists in localStorage across sessions. */}
      <AudioDeviceMenu />

      {/* User profile / sign-in — sticky to the right edge so it never scrolls out of view on mobile */}
      <div className="sticky -right-4 z-10 flex-shrink-0 pl-3 -mr-4 pr-4 h-full flex items-center bg-[#0e0e0e] shadow-[-12px_0_12px_-4px_rgba(14,14,14,0.95)]" data-testid="topbar-profile-pin">
        <ProfileCircle />
      </div>

      {/* Cloud Save Dialog — name / BPM / genre. Admin genre-tags feed the
          AI generator as Golden Guideline Master Templates (see backend
          _fetch_golden_guidelines). */}
      {showSaveDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}
          onClick={() => setShowSaveDialog(false)}
          data-testid="save-dialog-overlay"
        >
          <div
            className="rounded-2xl p-6 w-[400px] max-w-[92vw] space-y-4"
            style={{ background: "#0e0e0e", border: "1px solid #262626", boxShadow: "0 24px 60px rgba(0,0,0,0.6)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="save-dialog"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-base flex items-center gap-2">
                <CloudArrowUp size={16} weight="fill" /> Save Project to Cloud
              </h3>
              <button onClick={() => setShowSaveDialog(false)} className="text-white/40 hover:text-white/80" data-testid="save-dialog-close">
                <X size={14} weight="bold" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label-mono block mb-1">Project name</label>
                <input
                  type="text"
                  value={saveDialogName}
                  onChange={(e) => setSaveDialogName(e.target.value)}
                  placeholder="My Trap Beat"
                  autoFocus
                  className="w-full bg-[#080808] border border-[#262626] rounded px-2 py-2 text-sm focus:outline-none focus:border-[#444]"
                  data-testid="save-dialog-name"
                />
              </div>
              <div>
                <label className="label-mono block mb-1">BPM (current)</label>
                <input
                  type="number"
                  value={bpm}
                  readOnly
                  className="w-full bg-[#050505] border border-[#1a1a1a] rounded px-2 py-2 text-sm text-white/60"
                  data-testid="save-dialog-bpm"
                />
              </div>
              <div>
                <label className="label-mono block mb-1">Genre <span className="text-white/40">(admin → trains AI as Master Template)</span></label>
                <select
                  value={saveDialogGenre}
                  onChange={(e) => setSaveDialogGenre(e.target.value)}
                  className="w-full bg-[#080808] border border-[#262626] rounded px-2 py-2 text-sm focus:outline-none focus:border-[#444]"
                  data-testid="save-dialog-genre"
                >
                  <option value="pop">Pop</option>
                  <option value="trap">Trap</option>
                  <option value="rnb">RnB</option>
                  <option value="country">Country</option>
                  <option value="house">House</option>
                  <option value="afro">Afro</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setShowSaveDialog(false)}
                className="btn-3d px-4 py-2 text-xs"
                data-testid="save-dialog-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleCloudSave}
                disabled={saving || !saveDialogName.trim()}
                className="btn-3d btn-3d-accent px-4 py-2 text-xs font-bold disabled:opacity-40"
                data-testid="save-dialog-confirm"
              >
                {saving ? "Saving…" : "Save Project"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* v10.2 — Coming Soon badge REMOVED; DAW is now gated by the
          Producer Package subscription at the DAWPage level. */}

      {/* v19.0 — Producer Package paywall (trial slot exceeded). */}
      <ProducerPaywallModal
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        headline="You've already used your free beat."
        subline="Upgrade to the Producer Package to save unlimited projects to your cloud library."
      />
      {/* v19.7 — Floating shortcut list overlay. Portalled to body so it
          escapes the h-14 header clip and floats above every DAW pane
          without ever blocking pointer events on the surrounding UI. */}
      <ShortcutListOverlay open={showShortcuts} onClose={() => setShowShortcuts(false)} />
    </div>
  );
}

// v11.3 — Audio output device selector. Chrome/Edge only (Firefox lacks
// setSinkId on AudioContext) — falls back to a disabled state on
// unsupported browsers. Enumeration only returns full device labels
// AFTER any getUserMedia call has granted permission; we still show
// generic ids so the user can pick, we just can't display prose names
// until permission is granted. Selection persists in localStorage.
function AudioDeviceMenu() {
  const [open, setOpen] = React.useState(false);
  const [devices, setDevices] = React.useState([]);
  const [selected, setSelected] = React.useState(() => localStorage.getItem("crow_audio_sink_id") || "default");
  const [supported, setSupported] = React.useState(true);
  const [error, setError] = React.useState(null);
  const menuRef = React.useRef(null);

  // Detect setSinkId support once on mount.
  React.useEffect(() => {
    try {
      const ctx = Tone.getContext().rawContext;
      // AudioContext.setSinkId is Chrome 110+ / Edge 110+. Firefox=no.
      if (!ctx || typeof ctx.setSinkId !== "function") {
        setSupported(false);
      }
    } catch { setSupported(false); }
  }, []);

  const refreshDevices = React.useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const outs = all.filter((d) => d.kind === "audiooutput");
      setDevices(outs);
    } catch (e) {
      setError(e?.message || "Failed to enumerate audio devices");
    }
  }, []);

  // Refresh when the menu opens AND whenever the OS reports device changes
  // (headphones plugged in, Bluetooth speaker paired, etc).
  React.useEffect(() => {
    if (!open) return;
    refreshDevices();
  }, [open, refreshDevices]);
  React.useEffect(() => {
    const md = navigator.mediaDevices;
    if (!md?.addEventListener) return;
    const h = () => refreshDevices();
    md.addEventListener("devicechange", h);
    return () => md.removeEventListener("devicechange", h);
  }, [refreshDevices]);

  // Close the popover on outside-click.
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  // Re-apply the persisted sink on mount so the DAW respects the last
  // choice across restarts. Must wait for user gesture to init Tone.
  React.useEffect(() => {
    (async () => {
      if (!supported || !selected || selected === "default") return;
      try {
        // Tone.getContext() may not be running yet — that's fine, setSinkId
        // works on suspended contexts too.
        const ctx = Tone.getContext().rawContext;
        if (typeof ctx?.setSinkId === "function") {
          await ctx.setSinkId(selected);
        }
      } catch (e) {
        // Persisted device may not be present this session (e.g.,
        // Bluetooth speaker offline). Drop back to default silently.
        localStorage.removeItem("crow_audio_sink_id");
        setSelected("default");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported]);

  const handlePick = async (deviceId, label) => {
    setOpen(false);
    if (!supported) return;
    try {
      const ctx = Tone.getContext().rawContext;
      await ctx.setSinkId(deviceId);
      setSelected(deviceId);
      localStorage.setItem("crow_audio_sink_id", deviceId);
      toast.success(`Audio routed → ${label || "Selected device"}`);
    } catch (e) {
      toast.error("Failed to switch output: " + (e?.message || "unknown error"));
    }
  };

  return (
    <div ref={menuRef} className="relative flex-shrink-0">
      <button
        className="btn-3d w-10 h-10 flex items-center justify-center"
        onClick={() => setOpen((v) => !v)}
        title={supported ? "Audio output device" : "Audio device switching not supported in this browser"}
        data-testid="audio-settings-button"
      >
        <GearSix size={16} weight="bold" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-12 z-50 w-72 rounded-md py-2"
          style={{ background: "#0e0e0e", border: "1px solid #262626", boxShadow: "0 12px 32px rgba(0,0,0,0.7)" }}
          data-testid="audio-settings-menu"
        >
          <div className="px-3 pb-2 flex items-center gap-2 border-b border-[#1a1a1a]">
            <SpeakerHigh size={13} weight="fill" className="text-white/60" />
            <div className="text-[11px] font-semibold uppercase tracking-wider text-white/60">Audio Output</div>
          </div>
          {!supported && (
            <div className="px-3 py-3 text-[11px] text-amber-300 font-mono" data-testid="audio-settings-unsupported">
              Device switching requires Chrome/Edge 110+ (or the C.R.O.W. desktop app).
            </div>
          )}
          {supported && devices.length === 0 && (
            <div className="px-3 py-3 text-[11px] text-white/40 font-mono">
              No audio outputs enumerated yet.
              <button
                onClick={refreshDevices}
                className="ml-2 underline text-white/70 hover:text-white"
                data-testid="audio-settings-refresh"
              >Refresh</button>
            </div>
          )}
          {supported && devices.map((d) => {
            const isSel = d.deviceId === selected || (selected === "default" && d.deviceId === "");
            return (
              <button
                key={d.deviceId || "default"}
                onClick={() => handlePick(d.deviceId || "default", d.label)}
                className="w-full text-left px-3 py-2 text-xs hover:bg-[#1a1a1a] flex items-center gap-2"
                style={{ color: isSel ? "#4ade80" : "#e5e5e5" }}
                data-testid={`audio-device-${d.deviceId || "default"}`}
              >
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: isSel ? "#4ade80" : "transparent", border: isSel ? "none" : "1px solid #555" }} />
                <span className="truncate">{d.label || `Device ${(d.deviceId || "default").slice(0, 8)}`}</span>
              </button>
            );
          })}
          {error && (
            <div className="px-3 py-2 text-[10px] text-red-400 font-mono">{error}</div>
          )}
        </div>
      )}
    </div>
  );
}

function AutoSaveIndicator({ lastAutoSavedAt }) {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    if (!lastAutoSavedAt) return;
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 4000);
    return () => clearTimeout(t);
  }, [lastAutoSavedAt]);
  if (!lastAutoSavedAt) return null;
  return (
    <div
      className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-mono transition-opacity"
      style={{
        background: "#0a1a10",
        border: "1px solid #14532d",
        color: "#4ade80",
        opacity: visible ? 1 : 0.35,
      }}
      data-testid="autosave-indicator"
      title={`Auto-saved at ${new Date(lastAutoSavedAt).toLocaleTimeString()}`}
    >
      <CloudArrowUp size={11} weight="fill" /> Auto-saved
    </div>
  );
}
